import { useMemo } from 'react';
import styled from 'styled-components';
import { ProgressBar, ProgressBarContainer, ProgressFill, TrainingPrimaryButton } from '../../TrainingCard';
import type { Column } from '../../../../Table/Table';
import { NamedContentEntryText } from '../../../../ContentEntryText/NamedContentEntryText';
import { formatNumberWithCommas } from '../../../../../lib/numbers/formatNumberWithComma';
import { Icon, IconType } from '../../../../Props/Icon/Icon';
import { AvatarInitials } from '../../../../AvatarInitials';
import { SearchInput } from '../../../../SearchInput';
import { useNamedContentEntryText } from '../../../../../lib/hooks/contentEntry/useNamedContentEntryText';
import { HeaderCellWithSort } from '../../../../Table/components/HeaderCellWithSort/HeaderCellWithSort';
import type { TeamCompleationTrainingQueryParams } from '../../../../../apiHooks/useManagerTeamCompleationQuery';
import { getRankArrow } from '../../../../Leaderboard/components/UserRank/utils/getRankArrow';
import type { TeamCompletionUser } from '../../../../../lib/types/TeamCompletionUser';
import type { NudgeModalConfigs } from '../../../../Nudge/NudgeModal';
import { Ellipsis } from '../../../../utility/Ellipsis';

type TeamCompleationData = {
  columns: Column<TeamCompletionUser>[];
};

const getProgressBarColor = (progress: number) => {
  if (progress < 50) {
    return '#E93354';
  } else if (progress < 75) {
    return '#FF6F5A';
  } else if (progress === 100) {
    return '#3ABF87';
  } else {
    return undefined;
  }
};

export const useTeamTrainingCompletionData = (
  nameSearchInputValue: string,
  handleNameSearchChange: (searchTerm: string) => void,
  setNudgeModalConfigs: (configs: NudgeModalConfigs) => void,
  onNewParams: (params: Partial<TeamCompleationTrainingQueryParams>) => void,
  queryParams: TeamCompleationTrainingQueryParams,
): TeamCompleationData => {
  const namePlaceholder = useNamedContentEntryText({ refKey: 'managerTeamMembers', subKey: 'name' }) || '';

  return useMemo(
    () => ({
      columns: [
        {
          header: (
            <SearchInput
              value={nameSearchInputValue}
              onChange={handleNameSearchChange}
              placeholder={namePlaceholder}
              withSearchIcon
            />
          ),
          valueSelector: (user: TeamCompletionUser) => (
            <UserName>
              {user.avatar ? (
                <UserImg src={`data:image/png;base64,${user.avatar}`} />
              ) : (
                <AvatarInitials name={user.name || ''} id={user.name ?? ''} size="32px" />
              )}
              <Ellipsis>{user.name}</Ellipsis>
            </UserName>
          ),
          alignment: 'left',
          width: '1.5fr',
        },
        {
          header: <NamedContentEntryText container="span" refKey="managerTeamBehaviors" subKey="jobTitle" />,
          valueSelector: (user: TeamCompletionUser) => <UserPropTitle>{user.jobTitle}</UserPropTitle>,
        },
        {
          header: <NamedContentEntryText container="span" refKey="managerTeamBehaviors" subKey="department" />,
          valueSelector: (user: TeamCompletionUser) => <UserPropTitle>{user.departmentId}</UserPropTitle>,
        },
        {
          header: (
            <HeaderCellWithSort
              name={<NamedContentEntryText container="span" refKey="managerTeamBehaviors" subKey="cyberIQ" />}
              sortActive={queryParams.sort?.sortDirection !== undefined}
              sortDirection={queryParams.sort?.sortDirection === 'ascending' ? 0 : 1}
              onSortChange={() => {
                onNewParams({
                  sort: {
                    sortDirection: queryParams.sort?.sortDirection === 'ascending' ? 'descending' : 'ascending',
                    property: 'cyberIqScore',
                  },
                });
              }}
            />
          ),
          valueSelector: (user: TeamCompletionUser) => (
            <CyberIqDetails>
              {getRankArrow(user.rank || 0, user.previousRank || 0)}
              <UserPoints className="user-points">
                <Icon width={20} height={20} icon={IconType.COIN} />
                {formatNumberWithCommas(user.cyberIqPoints)}
              </UserPoints>
            </CyberIqDetails>
          ),
          alignment: 'left',
          width: '120px',
        },
        {
          header: <NamedContentEntryText container="span" refKey="managerTeamBehaviors" subKey="training" />,
          valueSelector: (user: TeamCompletionUser) => (
            <TrainingProgress>
              <ProgressBarContainer>
                <ProgressBar>
                  <ProgressFill
                    progress={
                      (user.trainingProgress.assignmentsCompleted / user.trainingProgress.totalAssignments) * 100 || 0
                    }
                    backgroundColor={getProgressBarColor(
                      (user.trainingProgress.assignmentsCompleted / user.trainingProgress.totalAssignments) * 100 || 0,
                    )}
                  />
                </ProgressBar>
              </ProgressBarContainer>
            </TrainingProgress>
          ),
        },
        {
          header: '',
          valueSelector: (user: TeamCompletionUser) =>
            user.trainingProgress.totalAssignments > 0 &&
            user.trainingProgress.totalAssignments !== user.trainingProgress.assignmentsCompleted ? (
              <TrainingPrimaryButton
                onClick={() =>
                  setNudgeModalConfigs({
                    isOpen: true,
                    userId: user.learnerId,
                  })
                }
              >
                <NamedContentEntryText container="span" refKey="managerTeamCyberSecurityTraining" subKey="nudge" />
              </TrainingPrimaryButton>
            ) : null,
          alignment: 'right',
          width: 'min-content',
        },
      ],
    }),
    [
      nameSearchInputValue,
      handleNameSearchChange,
      namePlaceholder,
      queryParams.sort?.sortDirection,
      onNewParams,
      setNudgeModalConfigs,
    ],
  );
};

const UserPoints = styled.div`
  font-size: ${({ theme }) => theme.fontSizes.medium};
  display: flex;
  align-items: center;
  gap: 8px;

  @media (max-width: 768px) {
    flex-direction: row-reverse;
    padding: 10px 8px;
    width: 90px;
    justify-content: flex-end;
    border-radius: ${({ theme }) => theme.borderRadius.extraLarge};
    background: ${({ theme }) => theme.colors.prototype.secondary};
  }
`;

const UserName = styled.div`
  width: 100%;
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: ${({ theme }) => theme.fontSizes.normal};
`;

const Column = styled.div`
  height: 100%;
  display: flex;
  justify-content: space-between;
  flex-direction: column;
  gap: 8px;
`;

const UserPropTitle = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: ${({ theme }) => theme.fontSizes.medium};
`;

const UserImg = styled.img`
  border-radius: 50%;
  width: 40px;
  height: 40px;
  cursor: pointer;
`;

const TrainingProgress = styled.div`
  width: 130px;
`;
const CyberIqDetails = styled.div`
  display: flex;
  align-items: center;
  grid-template-columns: none;
  gap: 4px;
`;
